import { useMemo } from 'react';
import type { ReactElement } from 'react';

import styled from 'styled-components';

import type { HeaderLogoComponentProps, HeaderLogoParams } from './types';

export const StyledWrapper = styled.div<{
  $isCollapsed: boolean;
}>(({
  $isCollapsed,

  theme: {
    colors,
    header,
    layout,
  },
}) => {
  const fullWidth = $isCollapsed
    ? layout.SIDEBAR_WIDTH_COLLAPSED
    : layout.SIDEBAR_WIDTH;

  return {
    boxSizing: 'border-box',
    display: 'flex',
    width: fullWidth,
    borderRightWidth: header.LOGO_BORDER_RIGHT_WIDTH || 0,
    borderRightStyle: 'solid',
    borderRightColor: header.LOGO_BORDER_RIGHT_COLOR
      ? colors[header.LOGO_BORDER_RIGHT_COLOR]
      : undefined,
  };
});

interface StyledLogoProps {
  $isCollapsed: boolean;
}

export const StyledLogo = styled.div.attrs((props: StyledLogoProps) => ({
  isCollapsed: props.$isCollapsed,
}))<StyledLogoProps>(({
  $isCollapsed,

  theme: {
    colors,
    fonts,
    header,
    layout,
  },
}) => {
  const offsetLeft = $isCollapsed
    ? header.LOGO_OFFSET_LEFT_COLLAPSED
    : header.LOGO_OFFSET_LEFT;

  const offsetRight = $isCollapsed
    ? header.LOGO_OFFSET_RIGHT_COLLAPSED
    : header.LOGO_OFFSET_RIGHT;

  return {
    ...fonts.H2,
    flex: 1,
    boxSizing: 'border-box',
    marginTop: 12,
    marginBottom: 12,
    marginLeft: offsetLeft,
    marginRight: offsetRight,
    height: layout.HEADER_HEIGHT - 2 * 12,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '0 50%',
    color: colors.NEUTRAL_DEFAULT,

    ':hover': {
      color: colors.NEUTRAL_DEFAULT,
    },

    ':active': {
      color: colors.NEUTRAL_DEFAULT,
    },
  };
});

const defaultComponentProps: Omit<HeaderLogoComponentProps, 'isCollapsed'> = {
  href: '/',
};

export type HeaderLogoProps = {
  isCollapsed: boolean;
  logo: HeaderLogoParams;
};

export function HeaderLogo({
  isCollapsed,

  logo: {
    component: HeaderComponent = 'a',
    componentProps = defaultComponentProps,
    getDefaultImage,
    getImage,
    img = undefined,
    imgCollapsed = undefined,
    title = undefined,
  },
}: HeaderLogoProps): ReactElement {
  const logoImage = useMemo(() => {
    if (getImage) {
      return getImage(isCollapsed);
    }

    if (isCollapsed && imgCollapsed) {
      return imgCollapsed;
    }

    return img;
  }, [
    getImage,
    img,
    imgCollapsed,
    isCollapsed,
  ]);
  const defaultLogoImage = useMemo(
    () => getDefaultImage?.(isCollapsed),
    [getDefaultImage, isCollapsed],
  );

  if (logoImage) {
    const backgroundImage = `url(${logoImage})${defaultLogoImage ? `, url(${defaultLogoImage})` : ''}`;

    return (
      <StyledWrapper
        $isCollapsed={isCollapsed}
      >
        <StyledLogo
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          as={HeaderComponent}
          {...componentProps}
          $isCollapsed={isCollapsed}
          style={{
            backgroundImage,
          }}
        />
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper
      $isCollapsed={isCollapsed}
    >
      <StyledLogo
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        as={HeaderComponent}
        {...componentProps}
        $isCollapsed={isCollapsed}
      >
        {title}
      </StyledLogo>
    </StyledWrapper>
  );
}
